import React, { useMemo, useState } from 'react';
import { Box, CircularProgress, Grid, Typography } from '@mui/material';
import { RecordVoiceOver, EditNote } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';

import { I18nNamespace } from '@/i18n/types/i18nNamespace';
import type { I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces';
import { useGetEvaluationProtocolsWithCategories, type TransformedProtocol } from '@/app/queries/evaluationProtocolQueries';
import type { EvaluationProtocolElementDto } from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from '../hooks/useScenarioForm';

import EvaluationTypeCard, { type EvaluationSummary } from './components/EvaluationTypeCard';
import EvaluationProtocolSelectionDialog from './components/EvaluationProtocolSelectionDialog';

interface ScenarioEvaluationProtocolsSettingsProps {
  scenarioId: number;
}

const buildSummary = (
  protocols: TransformedProtocol[],
  selectedElements: EvaluationProtocolElementDto[]
): EvaluationSummary => {
  const selectedIds = new Set(selectedElements.map((el) => el.id));
  const protocolNames: string[] = [];

  protocols.forEach((protocol) => {
    const hasSelection = protocol.categories?.some((category) =>
      category.elements?.some((element) => selectedIds.has(element.id))
    );

    if (hasSelection) {
      protocolNames.push(protocol.title);
    }
  });

  return {
    protocolCount: protocolNames.length,
    elementCount: selectedElements.length,
    protocolNames
  };
};

const ScenarioEvaluationProtocolsSettings: React.FC<ScenarioEvaluationProtocolsSettingsProps> = () => {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const { watch } = useFormContext<ScenarioSettingsType>();

  const { data: protocols, isLoading, isError } = useGetEvaluationProtocolsWithCategories();

  const notesEnabled = watch('notesEnabled');
  const conversationElements = watch('evaluationProtocolElements') || [];
  const notesElements = watch('notesEvaluationProtocolElements') || [];

  const [activeDialog, setActiveDialog] = useState<'conversation' | 'notes' | null>(null);

  const conversationSummary = useMemo(
    () => (protocols ? buildSummary(protocols, conversationElements) : { protocolCount: 0, elementCount: 0, protocolNames: [] }),
    [protocols, conversationElements]
  );

  const notesSummary = useMemo(
    () => (protocols ? buildSummary(protocols, notesElements) : { protocolCount: 0, elementCount: 0, protocolNames: [] }),
    [protocols, notesElements]
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !protocols) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <Typography color="error">
          {translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.errorLoading))}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 5, mb: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <EvaluationTypeCard
            title={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsConversationTitle))}
            description={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsConversationDescription))}
            summary={conversationSummary}
            icon={<RecordVoiceOver />}
            accentColor="#1976d2"
            emptyMessage={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsEmptyState))}
            configureLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsConfigure))}
            addCriteriaLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsAddCriteria))}
            protocolsLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsProtocolsLabel))}
            elementsLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsElementsLabel))}
            includedLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsIncludedLabel))}
            moreLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsMoreLabel))}
            onClick={() => setActiveDialog('conversation')}
          />
        </Grid>
        {notesEnabled && (
          <Grid item xs={12} md={6}>
            <EvaluationTypeCard
              title={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsNotesTitle))}
              description={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsNotesDescription))}
              summary={notesSummary}
              icon={<EditNote />}
              accentColor="#9c27b0"
              emptyMessage={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsEmptyState))}
              configureLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsConfigure))}
              addCriteriaLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsAddCriteria))}
              protocolsLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsProtocolsLabel))}
              elementsLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsElementsLabel))}
              includedLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsIncludedLabel))}
              moreLabel={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsMoreLabel))}
              onClick={() => setActiveDialog('notes')}
            />
          </Grid>
        )}
      </Grid>

      <EvaluationProtocolSelectionDialog
        open={activeDialog === 'conversation'}
        onClose={() => setActiveDialog(null)}
        title={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsConversationTitle))}
        description={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsConversationDescription))}
        protocols={protocols}
        fieldName="evaluationProtocolElements"
      />

      {notesEnabled && (
        <EvaluationProtocolSelectionDialog
          open={activeDialog === 'notes'}
          onClose={() => setActiveDialog(null)}
          title={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsNotesTitle))}
          description={translate(nameof.full<I18nScenarioEditorNs>((n) => n.scenarioSettingsDialog.evaluationProtocolsNotesDescription))}
          protocols={protocols}
          fieldName="notesEvaluationProtocolElements"
        />
      )}
    </Box>
  );
};

export default ScenarioEvaluationProtocolsSettings;
